import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { EnsApi, EnsName, EnsWallet } from '../../../application/ens/ports';
import { PARENT_ENS, TIERS } from '../../../domain/ens/ens';
import { useIsPhone } from '../../ByDevice';
import { C, EASE_OUT, hover, Lettering, RedesignFonts, Socials, Buy } from '../../kit';
import { Chapter, ChapterBody, Container, Copy, DrawnMenu, FlexCol, HeroBg, ProgressBar, Slide, SwipeChapter, ThumbAction } from '../../m2';
import {
  FieldBox,
  FieldLabel,
  Foot,
  Grid,
  InkButton,
  Note,
  Page,
  PageTitle,
  Panel,
  Prose,
  SceneHeader,
  SectionTitle,
  SUB_NAV,
  TextButton,
  Wrap,
  WalletChip,
} from '../shared';
import { EnsPageState, useEnsPage } from './useEnsPage';

/** /ens in the home page style: drawn header and ink panels on desktop, swipe chapters on a phone. */

export type EnsPorts = { api: EnsApi; wallet: EnsWallet; connect: () => void; disconnect: () => void };

const AVATAR = '/images/zeus-avatar.jpg';
const SCENE_WIDE = '/redesign2/pages/ens-wide.webp';
const SCENE_TALL = '/redesign2/pages/ens-tall.webp';

const LEAD = `Swap your 0x address for a name people can read, under ${PARENT_ENS}. Names of 10 characters or more are free.`;

const WHY = [
  'Easy to remember: send and receive with a name instead of a long address.',
  'Show you are part of the Zeus community, in every wallet that reads ENS.',
  'Minting needs no gas. You only pay the name price, if it has one.',
  'Short names are rare. Get them before anyone else and trade them on OpenSea.',
];

const DISCLAIMER =
  "This ENS service is run by the $ZEUS community for $ZEUS holders (contract 0x0f7dc5d02cc1e1f5ee47854d534d332a1081ccc8). The name \"Pepe's Dog Army\" is a community reference. It is not related to, affiliated with or endorsed by the Pepe's Dog brand, and we do not own or represent it.";

const chunk = <T,>(list: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

/** Each name gets its own avatar colour, so the wall of names does not repeat itself. */
const TILE_FILLS = [C.yellow, '#FFB3DE', '#A8E6B0', C.mist, '#FFD08A'];
const fillOf = (label: string) => TILE_FILLS[label.split('').reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) >>> 0, 7) % TILE_FILLS.length];

const countText = (n: number) => `${n.toLocaleString('en-US')} ${n === 1 ? 'name' : 'names'} minted`;

export const EnsScreen: React.FC<{ ports: EnsPorts }> = ({ ports }) => {
  const s = useEnsPage(ports);
  const phone = useIsPhone();
  return (
    <>
      <RedesignFonts />
      {phone ? <EnsMobile s={s} ports={ports} /> : <EnsDesktop s={s} ports={ports} />}
      {s.managing && <ManageDialog s={s} />}
    </>
  );
};

/* ================================================================== desktop */

const EnsDesktop: React.FC<{ s: EnsPageState; ports: EnsPorts }> = ({ s, ports }) => (
  <Page data-page="ens" id="top">
    <SceneHeader scene={SCENE_WIDE} sky="#3BAAFD" position="70% 30%" title={<PageTitle>GET YOUR ENS</PageTitle>} lead={LEAD}>
      <InkButton as="a" href="#mint" $big>
        MINT A NAME
      </InkButton>
    </SceneHeader>

    <Wrap id="mint" as={DeskSection} aria-labelledby="mint-title">
      <Grid $cols="minmax(0, 1.15fr) minmax(0, 0.85fr)">
        <div>
          <Panel $tilt={-0.6}>
            <SectionTitle id="mint-title">MINT A NAME</SectionTitle>
            <MintForm s={s} ports={ports} />
          </Panel>
          <Prose style={{ marginTop: 40 }}>
            <SectionTitle tone="pink" tilt={1.5}>
              WHY A NAME
            </SectionTitle>
            <ul>
              {WHY.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Prose>
        </div>
        <div>
          <PriceList s={s} />
          <img className="zeus-sit" src="/redesign2/scenes/zeus-sit.webp" alt="" aria-hidden="true" />
        </div>
      </Grid>
    </Wrap>

    {s.connected && (
      <Wrap as="section" id="yours" aria-label="Your names" style={{ paddingTop: 0 }}>
        <SectionTitle tilt={1.5} tone="pink">YOUR NAMES</SectionTitle>
        <YourNames s={s} />
      </Wrap>
    )}

    <Wrap as="section" id="recent" aria-labelledby="recent-title" style={{ paddingTop: 0 }}>
      <Heading>
        <SectionTitle id="recent-title">LATEST NAMES</SectionTitle>
        {s.recent.status !== 'error' && <Count>{countText(s.recent.data.total)}</Count>}
      </Heading>
      <Recent s={s} />
      <Small>{DISCLAIMER}</Small>
    </Wrap>

    <Foot />
  </Page>
);

/* ================================================================== phone */

const EnsMobile: React.FC<{ s: EnsPageState; ports: EnsPorts }> = ({ s, ports }) => {
  const ref = useRef<HTMLDivElement>(null);
  const active = useActiveChapter(ref);
  const recent = s.recent.data.names;
  const groups = chunk(recent, 3);
  return (
    <Container ref={ref} style={{ fontFamily: "'Ubuntu', sans-serif" }} data-page="ens">
      <ProgressBar active={active} total={5} />

      <Chapter className="chapter" data-index={0} bg="#3BAAFD" id="top">
        <HeroBg src={SCENE_TALL} alt="" style={{ objectPosition: '62% 70%' }} />
        <ChapterBody style={{ padding: 'calc(env(safe-area-inset-top) + 84px) 18px 0' }}>
          <DrawnMenu items={SUB_NAV} />
          <h1 style={{ margin: '0 0 14px' }}>
            <Lettering $size="clamp(44px, 13vw, 60px)" $tilt={-3}>
              GET YOUR ENS
            </Lettering>
          </h1>
          <Copy $big $shadow style={{ color: C.white, maxWidth: '24ch' }}>
            Your 0x address as a name people can read. 10 characters or more are free.
          </Copy>
        </ChapterBody>
        <ThumbAction>
          <InkButton as="a" href="#mint" $big>
            MINT A NAME
          </InkButton>
        </ThumbAction>
      </Chapter>

      <SwipeChapter id="mint" index={1} bg={C.paper}>
        <Slide>
          <MobileTitle>MINT A NAME</MobileTitle>
          <MintForm s={s} ports={ports} />
        </Slide>
        <Slide $center>
          <PriceList s={s} />
          <Small style={{ margin: 0 }}>{DISCLAIMER}</Small>
        </Slide>
        <Slide $center bg="/redesign2/scenes/house.webp" bgPosition="72% 50%">
          <MobileTitle>WHY A NAME</MobileTitle>
          <Prose>
            <ul>
              {WHY.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Prose>
        </Slide>
      </SwipeChapter>

      <SwipeChapter id="yours" index={2} bg={C.mist}>
        {!s.connected ? (
          <Slide $center>
            <MobileTitle>YOUR NAMES</MobileTitle>
            <Copy>Connect your wallet to see your names, point them to another address or give them an avatar.</Copy>
            <InkButton type="button" onClick={ports.connect} style={{ alignSelf: 'center' }}>
              Connect wallet
            </InkButton>
          </Slide>
        ) : s.mine.status === 'ready' && s.mine.data.length === 0 ? (
          <Slide $center>
            <MobileTitle>YOUR NAMES</MobileTitle>
            <Copy>No names on this wallet yet. Swipe back up and mint your first one.</Copy>
          </Slide>
        ) : s.mine.status === 'error' ? (
          <Slide $center>
            <MobileTitle>YOUR NAMES</MobileTitle>
            <Note tone="bad">Your names could not load.</Note>
            <InkButton type="button" $tone="white" onClick={s.loadMine} style={{ alignSelf: 'center' }}>
              Try again
            </InkButton>
          </Slide>
        ) : (
          (s.mine.status === 'loading' && s.mine.data.length === 0 ? [[]] : chunk(s.mine.data, 4)).map((g, i) => (
            <Slide key={i} $center>
              <MobileTitle>YOUR NAMES</MobileTitle>
              {g.length === 0 ? (
                <Note tone="info">Loading your names...</Note>
              ) : (
                <Stack>
                  {g.map((n) => (
                    <NameTile key={n.name} n={n} onManage={() => s.openManage(n)} />
                  ))}
                </Stack>
              )}
            </Slide>
          ))
        )}
      </SwipeChapter>

      <SwipeChapter id="recent" index={3} bg={C.yellow}>
        {s.recent.status === 'error' ? (
          <Slide $center>
            <MobileTitle>LATEST NAMES</MobileTitle>
            <Note tone="bad">The latest names could not load.</Note>
            <InkButton type="button" $tone="white" onClick={s.loadRecent} style={{ alignSelf: 'center' }}>
              Try again
            </InkButton>
          </Slide>
        ) : groups.length === 0 ? (
          <Slide $center>
            <MobileTitle>LATEST NAMES</MobileTitle>
            {s.recent.status === 'loading' ? <Note tone="info">Loading...</Note> : <Copy>No names yet. Be the first.</Copy>}
          </Slide>
        ) : (
          groups.map((g, i) => (
            <Slide key={i} $center>
              <MobileTitle>LATEST NAMES</MobileTitle>
              {i === 0 && <Count>{countText(s.recent.data.total)}</Count>}
              <Stack>
                {g.map((n) => (
                  <NameTile key={n.name} n={n} />
                ))}
              </Stack>
            </Slide>
          ))
        )}
      </SwipeChapter>

      <Chapter className="chapter" data-index={4} bg={C.ink} id="join">
        <HeroBg src="/redesign2/scenes/gang.webp" alt="" style={{ objectFit: 'contain', objectPosition: 'center 30%' }} />
        <ThumbAction>
          <FlexCol>
            <Buy big />
            <Socials color={C.white} />
            <small style={{ color: C.white, fontFamily: 'Ubuntu', marginTop: 10, fontWeight: 500 }}>© 2026 Pepe's Dog CTO</small>
          </FlexCol>
        </ThumbAction>
      </Chapter>
    </Container>
  );
};

/** Which full screen chapter is in front, for the progress bar. */
function useActiveChapter(ref: React.RefObject<HTMLDivElement | null>) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(Number(e.target.getAttribute('data-index')))),
      { threshold: 0.5 }
    );
    ref.current?.querySelectorAll('section.chapter').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ref]);
  return active;
}

/* ================================================================== parts */

const MintForm: React.FC<{ s: EnsPageState; ports: EnsPorts }> = ({ s, ports }) => {
  const { price } = s;
  const action = !s.connected ? null : price.usd > 0 ? `Mint for $${price.usd}` : 'Mint for free';
  return (
    <form
      aria-label="Mint a name"
      onSubmit={(e) => {
        e.preventDefault();
        if (s.connected && s.label && !s.busy) s.mint();
      }}
    >
      {s.connected && s.address && <WalletChip address={s.address} onDisconnect={ports.disconnect} disabled={s.busy} />}
      <FieldLabel htmlFor="ens-name">Your name</FieldLabel>
      <FieldBox>
        <input
          id="ens-name"
          value={s.label}
          onChange={(e) => s.setLabel(e.target.value)}
          placeholder="yourname"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={63}
          disabled={s.busy}
          enterKeyHint="go"
        />
        <span className="suffix">.{PARENT_ENS}</span>
      </FieldBox>
      <PriceLine aria-live="polite">
        {s.label ? (
          price.usd === 0 ? (
            <>
              <strong>Free</strong>
              <span>one free name per wallet</span>
            </>
          ) : (
            <>
              <strong>${price.usd}</strong>
              <span>about {price.eth.toFixed(4)} ETH</span>
            </>
          )
        ) : (
          <span>Letters, numbers and hyphens.</span>
        )}
      </PriceLine>
      <Actions>
        {!s.connected ? (
          <InkButton type="button" onClick={ports.connect}>
            Connect wallet
          </InkButton>
        ) : (
          <InkButton type="submit" disabled={!s.label || s.busy}>
            {s.busy ? 'Working...' : action}
          </InkButton>
        )}
      </Actions>
      {s.mintMsg && <Note tone={s.mintMsg.tone}>{s.mintMsg.text}</Note>}
    </form>
  );
};

const PriceList: React.FC<{ s: EnsPageState }> = ({ s }) => (
  <Panel $tone="yellow" $tilt={1.2}>
    <PriceTitle>PRICES</PriceTitle>
    <Prices>
      {TIERS.map((t) => (
        <li key={t.tier} data-on={s.price.tier === t.tier}>
          <span>{t.label}</span>
          <strong>{t.usd === 0 ? 'FREE' : `$${t.usd}`}</strong>
        </li>
      ))}
    </Prices>
    <PriceNote>Paid in ETH at the current price. Minting itself needs no gas.</PriceNote>
  </Panel>
);

const YourNames: React.FC<{ s: EnsPageState }> = ({ s }) => {
  if (s.mine.status === 'error')
    return (
      <>
        <Note tone="bad">Your names could not load.</Note>
        <InkButton type="button" $tone="white" onClick={s.loadMine} style={{ marginTop: 16 }}>
          Try again
        </InkButton>
      </>
    );
  if (s.mine.status === 'loading' && s.mine.data.length === 0) return <Note tone="info">Loading your names...</Note>;
  if (s.mine.data.length === 0) return <Prose>No names on this wallet yet. Mint your first one above.</Prose>;
  return (
    <Tiles>
      {s.mine.data.map((n) => (
        <NameTile key={n.name} n={n} onManage={() => s.openManage(n)} />
      ))}
    </Tiles>
  );
};

const Recent: React.FC<{ s: EnsPageState }> = ({ s }) => {
  if (s.recent.status === 'error')
    return (
      <>
        <Note tone="bad">The latest names could not load.</Note>
        <InkButton type="button" $tone="white" onClick={s.loadRecent} style={{ marginTop: 16 }}>
          Try again
        </InkButton>
      </>
    );
  if (s.recent.status === 'loading' && s.recent.data.names.length === 0) return <Note tone="info">Loading...</Note>;
  if (s.recent.data.names.length === 0) return <Prose>No names yet. Be the first.</Prose>;
  return (
    <Tiles>
      {s.recent.data.names.map((n) => (
        <NameTile key={n.name} n={n} />
      ))}
    </Tiles>
  );
};

const NameTile: React.FC<{ n: EnsName; onManage?: () => void; big?: boolean }> = ({ n, onManage, big }) => (
  <Tile $big={big} style={{ ['--fill' as string]: fillOf(n.label) }}>
    <img
      src={n.avatar || AVATAR}
      alt=""
      loading="lazy"
      onError={(e) => {
        const img = e.target as HTMLImageElement;
        if (!img.src.endsWith(AVATAR)) img.src = AVATAR;
      }}
    />
    <div className="name">
      <strong title={n.label}>{n.label}</strong>
      <span>.{PARENT_ENS}</span>
    </div>
    {onManage && (
      <InkButton type="button" $tone="white" onClick={onManage} aria-label={`Manage ${n.label}`} style={{ flex: 'none', minHeight: 44, padding: '8px 16px', fontSize: 15 }}>
        Manage
      </InkButton>
    )}
  </Tile>
);

const ManageDialog: React.FC<{ s: EnsPageState }> = ({ s }) => {
  const n = s.managing!;
  const [to, setTo] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const close = () => !s.manageBusy && s.openManage(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  useEffect(() => {
    if (!file || !URL.createObjectURL) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <Overlay onClick={close}>
      <Sheet role="dialog" aria-modal="true" aria-labelledby="manage-title" onClick={(e) => e.stopPropagation()}>
        <h2 id="manage-title">
          <Lettering $size="clamp(24px, 6vw, 34px)">MANAGE</Lettering>
          <span className="full" title={n.name}>
            {n.name}
          </span>
        </h2>
        <p className="now">
          Points to <code>{n.address ? n.address : 'no address yet'}</code>
        </p>

        <FieldLabel htmlFor="manage-to">New address</FieldLabel>
        <FieldBox>
          <input id="manage-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="0x..." autoComplete="off" spellCheck={false} disabled={s.manageBusy} />
        </FieldBox>
        <InkButton type="button" onClick={() => s.moveTo(to)} disabled={!to || s.manageBusy} style={{ marginTop: 14 }}>
          Update address
        </InkButton>

        <hr />

        <FieldLabel htmlFor="manage-avatar">Avatar</FieldLabel>
        <div className="avatar">
          <img src={preview || n.avatar || AVATAR} alt="" />
          <label className="pick">
            Choose an image
            <input id="manage-avatar" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={s.manageBusy} />
          </label>
        </div>
        <InkButton type="button" onClick={() => file && s.setAvatar(file)} disabled={!file || s.manageBusy} style={{ marginTop: 14 }}>
          Update avatar
        </InkButton>

        {s.manageMsg && <Note tone={s.manageMsg.tone}>{s.manageMsg.text}</Note>}
        <TextButton type="button" className="close" onClick={close} disabled={s.manageBusy}>
          Close
        </TextButton>
      </Sheet>
    </Overlay>
  );
};

/* ================================================================== styles */

const Heading = styled.div`
  display: flex;
  align-items: baseline;
  gap: 20px;
  flex-wrap: wrap;
  h2 {
    margin-bottom: 18px !important;
  }
`;

const Count = styled.p`
  align-self: flex-start;
  margin: 0 0 18px;
  padding: 4px 14px;
  background: ${C.white};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  box-shadow: 2px 3px 0 ${C.ink};
  font-family: 'GROBOLD', sans-serif;
  font-size: 18px;
`;

const MobileTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 style={{ margin: 0 }}>
    <Lettering $size="clamp(30px, 9vw, 40px)">{children}</Lettering>
  </h2>
);

const PriceLine = styled.p`
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-height: 34px;
  margin: 12px 0 0;
  font-size: 16px;
  strong {
    font-family: 'GROBOLD', sans-serif;
    font-size: 26px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 16px;
`;


const PriceTitle = styled.p`
  margin: 0 0 12px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 26px;
`;

const Prices = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    padding: 12px 14px;
    border-bottom: 3px dashed rgba(11, 19, 43, 0.35);
    border-radius: 14px;
    font-weight: 700;
    font-size: 17px;
    transition: background 200ms ${EASE_OUT}, transform 200ms ${EASE_OUT};
  }
  li:last-child {
    border-bottom: 0;
  }
  li[data-on='true'] {
    background: ${C.white};
    transform: rotate(-1deg) scale(1.02);
    box-shadow: 3px 4px 0 ${C.ink};
  }
  strong {
    font-family: 'GROBOLD', sans-serif;
    font-size: 24px;
    white-space: nowrap;
  }
`;

const PriceNote = styled.p`
  margin: 14px 0 0;
  font-size: 15px;
  font-weight: 500;
`;

const Tiles = styled.ul.attrs({ role: 'list' })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 22px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Tile = styled.li.attrs({ role: 'listitem' })<{ $big?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 14px 16px;
  background: ${C.white};
  border: 3.5px solid ${C.ink};
  border-radius: 22px 28px 20px 30px / 26px 20px 28px 22px;
  box-shadow: 4px 5px 0 ${C.ink};
  transition: transform 260ms ${EASE_OUT}, box-shadow 260ms ${EASE_OUT};
  ${(p) =>
    p.$big &&
    css`
      flex-wrap: wrap;
      .name {
        flex-basis: calc(100% - 100px);
      }
      button {
        flex: 1 1 100% !important;
      }
    `}
  ${hover(css`
    &:hover {
      transform: translate(-1px, -2px) rotate(-0.8deg);
      box-shadow: 6px 8px 0 ${C.ink};
    }
  `)}
  img {
    flex: none;
    width: ${(p) => (p.$big ? '84px' : '56px')};
    height: ${(p) => (p.$big ? '84px' : '56px')};
    border-radius: 50%;
    border: 3px solid ${C.ink};
    object-fit: cover;
    background: var(--fill, ${C.mist});
  }
  &:nth-child(odd) {
    transform: rotate(-0.6deg);
  }
  &:nth-child(even) {
    transform: rotate(0.5deg);
  }
  .name {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  strong {
    font-family: 'GROBOLD', sans-serif;
    font-size: ${(p) => (p.$big ? '26px' : '20px')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    font: 700 14px 'Ubuntu Mono', monospace;
    opacity: 0.75;
  }
`;

const DeskSection = styled.section`
  .zeus-sit {
    display: block;
    width: min(300px, 70%);
    margin: 28px auto 0;
    transform: rotate(3deg);
  }
`;

const Small = styled.p`
  max-width: 70ch;
  margin: 48px 0 0;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.75;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(11, 19, 43, 0.62);
  @media (max-width: 760px) {
    align-items: end;
    padding: 0;
  }
`;

const Sheet = styled.div`
  position: relative;
  width: min(520px, 100%);
  max-height: calc(100dvh - 32px);
  overflow: auto;
  padding: 28px 26px 18px;
  background: ${C.paper};
  color: ${C.ink};
  border: 4px solid ${C.ink};
  border-radius: 28px 36px 24px 40px / 34px 26px 38px 28px;
  box-shadow: 6px 8px 0 ${C.ink};
  h2 {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 0 8px;
  }
  .full {
    font: 700 18px 'Ubuntu Mono', monospace;
    overflow-wrap: anywhere;
  }
  .now {
    margin: 0 0 18px;
    font-size: 14px;
    code {
      font: 700 13px 'Ubuntu Mono', monospace;
      overflow-wrap: anywhere;
    }
  }
  hr {
    margin: 24px 0 18px;
    border: 0;
    border-top: 3px dashed rgba(11, 19, 43, 0.35);
  }
  .avatar {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .avatar img {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 3px solid ${C.ink};
    object-fit: cover;
  }
  .pick {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 8px 18px;
    border: 3px dashed ${C.ink};
    border-radius: 999px;
    font-weight: 700;
    cursor: pointer;
  }
  .pick input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }
  .pick:focus-within {
    outline: 3px dashed ${C.ink};
    outline-offset: 4px;
  }
  .close {
    display: block;
    margin: 14px auto 0;
  }
  @media (max-width: 760px) {
    width: 100%;
    max-height: 92dvh;
    border-radius: 28px 36px 0 0;
    padding-bottom: calc(env(safe-area-inset-bottom) + 18px);
  }
`;
